import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material"
import { MoreVert as MoreVertIcon } from "@mui/icons-material"
import { useState } from "react"

const JobsTable = ({
  jobs = [],
  columns = [],
  actions = [],
  onActionClick,
  rowsPerPageOptions = [10, 25, 50],
  defaultRowsPerPage = 10,
  emptyMessage = "No jobs found",
  // Optional server-side pagination props
  serverSidePagination = false,
  totalCount,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
}) => {

  // console.log("JobsTable jobs",jobs)

  const [internalPage, setInternalPage] = useState(0)
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedJob, setSelectedJob] = useState(null)

  const page = serverSidePagination ? (controlledPage ?? 0) : internalPage
  const rowsPerPage = serverSidePagination ? (controlledRowsPerPage ?? defaultRowsPerPage) : internalRowsPerPage

  const handleChangePage = (event, newPage) => {
    if (serverSidePagination) {
      onPageChange?.(event, newPage)
    } else {
      setInternalPage(newPage)
    }
  }

  const handleChangeRowsPerPage = (event) => {
    if (serverSidePagination) {
      onRowsPerPageChange?.(event)
    } else {
      setInternalRowsPerPage(Number.parseInt(event.target.value, 10))
      setInternalPage(0)
    }
  }

  const handleMenuClick = (event, job) => {
    setAnchorEl(event.currentTarget)
    setSelectedJob(job)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleAction = (action) => {
    if (onActionClick) {
      onActionClick(action, selectedJob)
    }
    handleMenuClose()
  }

  const getStatusColor = (status) => {
    const statusColors = {
      completed: "success",
      processing: "primary",
      new: "warning",
      cancelled: "error",
      closed: "default",
    }
    return statusColors[status] || "default"
  }

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept On Hold",
    }
    return labels[subStatus] || subStatus
  }

  const renderCellContent = (column, job) => {
    const value = column.field ? job[column.field] : null

    // Custom render function
    if (column.render) {
      return column.render(job, value)
    }

    // Default rendering based on field type
    switch (column.type) {
      case "status":
        return (
          <Chip
            label={value}
            color={getStatusColor(value)}
            size="small"
          />
        )
      case "subStatus":
        return value ? (
          <Chip label={getSubStatusLabel(value)} color="warning" size="small" />
        ) : (
          <Chip label="None" variant="outlined" size="small" />
        )
      case "date":
        return value ? new Date(value).toLocaleDateString() : "N/A"
      case "currency":
        return value ? `₹${value.toLocaleString("en-IN")}` : "₹0"
      case "jobId":
        return (
          <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
            {value}
          </Typography>
        )
      default:
        return value || "N/A"
    }
  }

  const paginatedJobs = serverSidePagination
    ? jobs  // backend already sliced
    : jobs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
              {actions.length > 0 && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.map((job) => {
              const jobId = job._id || job.JobId
              return (
                <TableRow key={jobId} hover>
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align || "left"}>
                      {renderCellContent(column, job)}
                    </TableCell>
                  ))}
                  {actions.length > 0 && (
                    <TableCell>
                      <Tooltip title="More actions">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, job)}
                          size="small"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && (selectedJob?._id === jobId || selectedJob?.JobId === jobId)}
                        onClose={handleMenuClose}
                      >
                        {actions.map((action) => (
                          <MenuItem
                            key={action.id}
                            onClick={() => handleAction(action)}
                            sx={action.color ? { color: `${action.color}.main` } : {}}
                          >
                            {action.icon && (
                              <span style={{ marginRight: 8, display: "flex" }}>
                                {action.icon}
                              </span>
                            )}
                            {action.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
            {paginatedJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (actions.length > 0 ? 1 : 0)} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={serverSidePagination ? (totalCount ?? 0) : jobs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Paper>
  )
}

export default JobsTable

// ============================================
// File: pages/ProcessingJobsPage.jsx (Example Usage)
// ============================================
// import { useState } from "react"
// import { Box, Typography } from "@mui/material"
// import {
//   VisibilityIcon,
//   EditIcon,
//   CommentIcon,
// } from "@mui/icons-material"
// import { useApiQuery } from "../../hooks/useApi"
// import JobsTable from "../../components/jobs/JobsTable"
// import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"

// function ProcessingJobsPageExample() {
//   const [viewDialogOpen, setViewDialogOpen] = useState(false)
//   const [selectedJob, setSelectedJob] = useState(null)

//   const { data: jobs, isLoading } = useApiQuery(
//     ["processing-jobs"],
//     `/jobs/processing/`
//   )

//   // Define table columns
//   const columns = [
//     { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
//     { id: "client", label: "Client", field: "clientName" },
//     { id: "jobType", label: "Job Type", field: "jobType" },
//     { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
//     { id: "status", label: "Status", field: "status", type: "status" },
//     { id: "subStatus", label: "Sub Status", field: "subStatus", type: "subStatus" },
//   ]

//   // Define actions
//   const actions = [
//     {
//       id: "view",
//       label: "View Details",
//       icon: <VisibilityIcon />,
//     },
//     {
//       id: "setStatus",
//       label: "Set Status",
//       icon: <EditIcon />,
//     },
//     {
//       id: "setSubStatus",
//       label: "Set Sub Status",
//       icon: <EditIcon />,
//     },
//     {
//       id: "addComment",
//       label: "Add Comment",
//       icon: <CommentIcon />,
//     },
//   ]

//   const handleActionClick = (action, job) => {
//     switch (action.id) {
//       case "view":
//         setSelectedJob(job)
//         setViewDialogOpen(true)
//         break
//       case "setStatus":
//         // Open status dialog
//         console.log("Set status for", job)
//         break
//       case "setSubStatus":
//         // Open sub-status dialog
//         console.log("Set sub-status for", job)
//         break
//       case "addComment":
//         // Open comment dialog
//         console.log("Add comment for", job)
//         break
//       default:
//         break
//     }
//   }

//   if (isLoading) {
//     return <div>Loading....</div>
//   }

//   return (
//     <Box>
//       <Typography variant="h4" component="h1" gutterBottom>
//         All Processing Jobs
//       </Typography>
//       <Typography variant="body1" color="textSecondary" paragraph>
//         These are all accepted jobs.
//       </Typography>

//       <JobsTable
//         jobs={jobs || []}
//         columns={columns}
//         actions={actions}
//         onActionClick={handleActionClick}
//         emptyMessage="No jobs in this status"
//       />

//       <JobDetailsDialog
//         open={viewDialogOpen}
//         onClose={() => setViewDialogOpen(false)}
//         job={selectedJob}
//       />
//     </Box>
//   )
// }

// export default ProcessingJobsPageExample